import { MutationKey, useMutation, UseMutationOptions } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import { HttpException } from '../exceptions/index';
import { StringKey } from '../lang/index';
import { useLocale } from './use-locale.hook';

const FALLBACK_ERROR_MESSAGE = 'Request failed';

type AppMutationProps<
  TData = unknown,
  TError = HttpException,
  TVariables = void,
  TContext = unknown,
> = {
  defaultErrorHandling?: boolean;
} & Omit<UseMutationOptions<TData, TError, TVariables, TContext>, 'mutationKey'>;

export const useAppMutation = <
  TData = unknown,
  TError = HttpException,
  TVariables = void,
  TContext = unknown,
>(
  mutationKey: MutationKey = [],
  { defaultErrorHandling = true, ...params }: AppMutationProps<TData, TError, TVariables, TContext>,
) => {
  const { messagesLocale } = useLocale();
  return useMutation({
    mutationKey,
    ...params,
    onError: (error, variables, context) => {
      if (defaultErrorHandling && error instanceof HttpException) {
        const { body } = error;
        const messageKey =
          typeof body?.message === 'string' ? (body.message as StringKey) : undefined;
        const localizedMessage = messageKey ? messagesLocale?.[messageKey] : undefined;
        const capitalizedLocalizedMessage = localizedMessage
          ? localizedMessage[0]?.toUpperCase() + localizedMessage.slice(1)
          : undefined;
        const normalizedMessage =
          capitalizedLocalizedMessage || body?.message || error.message || FALLBACK_ERROR_MESSAGE;
        toast(`${normalizedMessage}`, {
          position: 'bottom-center',
          type: 'error',
          theme: 'light',
        });
      }

      params.onError?.(error, variables, context);
    },
  });
};
